export default function useApiAttributes() {
  const { t } = useI18n()
  const { segmentation } = useSegmentation()

  const getCompanyAttributes = (params: {
    parent?: string
    simplified?: boolean
    specialFields?: string[]
    version: ApiVersion
  }) => {
    const parent = params.parent || 'company'
    const specialFields = params.specialFields || []

    let attributes: ApiAttribute[] = []

    if (params.version === 'v1') {
      attributes.push(
        {
          description: 'api.company.v1.businessType',
          example: 'public-company',
          name: 'businessType',
          parent: `${parent}`,
          type: 'string',
          values: [
            'educational-institution',
            'government-agency',
            'nonprofit',
            'partnership',
            'privately-held',
            'public-company',
            'self-employed',
            'sole-proprietorship',
          ],
        },
        {
          description: 'api.company.v1.city',
          name: 'city',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.cityAddress',
              example: '410 Terry Ave N',
              name: 'address',
              parent: `${parent}.city`,
              type: 'string',
            },
            {
              description: 'api.company.v1.cityCode',
              example: 'seattle|washington|us',
              name: 'code',
              parent: `${parent}.city`,
              type: 'string',
            },
            {
              description: 'api.company.v1.cityLatitude',
              example: '47.6038321',
              name: 'latitude',
              parent: `${parent}.city`,
              type: 'string',
            },
            {
              description: 'api.company.v1.cityLongitude',
              example: '-122.3300624',
              name: 'longitude',
              parent: `${parent}.city`,
              type: 'string',
            },
            {
              description: 'api.company.v1.cityName',
              example: 'Seattle',
              name: 'name',
              parent: `${parent}.city`,
              type: 'string',
            },
            {
              description: 'api.company.v1.cityPostcode',
              example: '98101',
              name: 'postcode',
              parent: `${parent}.city`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.codeNaics',
          example: '518210',
          name: 'codeNaics',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.codeSic',
          example: '5961',
          name: 'codeSic',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.companiesAcquisitions',
          name: 'companiesAcquisitions',
          parent: `${parent}`,
          type: 'array',

          attributes: [
            {
              description: 'api.company.v1.companiesAcquisitionsDescriptionShort',
              name: 'descriptionShort',
              parent: `${parent}.companiesAcquisitions`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesAcquisitionsDomain',
              name: 'domain',
              parent: `${parent}.companiesAcquisitions`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesAcquisitionsId',
              name: 'id',
              parent: `${parent}.companiesAcquisitions`,
              type: 'number',
            },
            {
              description: 'api.company.v1.companiesAcquisitionsName',
              name: 'name',
              parent: `${parent}.companiesAcquisitions`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.companiesSubsidiaries',
          name: 'companiesSubsidiaries',
          parent: `${parent}`,
          type: 'array',

          attributes: [
            {
              description: 'api.company.v1.companiesSubsidiariesDescriptionShort',
              name: 'descriptionShort',
              parent: `${parent}.companiesSubsidiaries`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesSubsidiariesDomain',
              name: 'domain',
              parent: `${parent}.companiesSubsidiaries`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesSubsidiariesId',
              name: 'id',
              parent: `${parent}.companiesSubsidiaries`,
              type: 'number',
            },
            {
              description: 'api.company.v1.companiesSubsidiariesName',
              name: 'name',
              parent: `${parent}.companiesSubsidiaries`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.companiesSimilar',
          name: 'companiesSimilar',
          parent: `${parent}`,
          type: 'array',

          attributes: [
            {
              description: 'api.company.v1.companiesSimilarDescriptionShort',
              name: 'descriptionShort',
              parent: `${parent}.companiesSimilar`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesSimilarDomain',
              name: 'domain',
              parent: `${parent}.companiesSimilar`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesSimilarId',
              name: 'id',
              parent: `${parent}.companiesSimilar`,
              type: 'number',
            },
            {
              description: 'api.company.v1.companiesSimilarName',
              name: 'name',
              parent: `${parent}.companiesSimilar`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companiesSimilarSimilitude',
              name: 'similitude',
              parent: `${parent}.companiesSimilar`,
              type: 'number',
            },
          ],
        },
        {
          description: 'api.company.v1.companyParent',
          name: 'companyParent',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.companyParentDescriptionShort',
              name: 'descriptionShort',
              parent: `${parent}.companyParent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companyParentDomain',
              name: 'domain',
              parent: `${parent}.companyParent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companyParentId',
              name: 'id',
              parent: `${parent}.companyParent`,
              type: 'number',
            },
            {
              description: 'api.company.v1.companyParentName',
              name: 'name',
              parent: `${parent}.companyParent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.companyParentSocialNetworks',
              name: 'socialNetworks',
              parent: `${parent}.companyParent`,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v1.companyParentSocialNetworksDribbble',
                  name: 'dribbble',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksFacebook',
                  name: 'facebook',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksGithub',
                  name: 'github',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksInstagram',
                  name: 'instagram',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksLinkedin',
                  name: 'linkedin',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksPinterest',
                  name: 'pinterest',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksSnapchat',
                  name: 'snapchat',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksTiktok',
                  name: 'tiktok',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksTwitter',
                  name: 'twitter',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksWellfound',
                  name: 'wellfound',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
                {
                  description: 'api.company.v1.companyParentSocialNetworksYoutube',
                  name: 'youtube',
                  parent: `${parent}.companyParent.socialNetworks`,
                  type: 'string',
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v1.continent',
          name: 'continent',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.continentCode',
              example: 'na',
              name: 'code',
              parent: `${parent}.continent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.continentLatitude',
              example: '51.0000002',
              name: 'latitude',
              parent: `${parent}.continent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.continentLongitude',
              example: '-109',
              name: 'longitude',
              parent: `${parent}.continent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.continentName',
              example: 'North America',
              name: 'name',
              parent: `${parent}.continent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.continentNameEs',
              example: 'América del Norte',
              name: 'nameEs',
              parent: `${parent}.continent`,
              type: 'string',
            },
            {
              description: 'api.company.v1.continentNameFr',
              example: 'Amérique du Nord',
              name: 'nameFr',
              parent: `${parent}.continent`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.country',
          name: 'country',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.countryCode',
              example: 'us',
              name: 'code',
              parent: `${parent}.country`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countryLatitude',
              example: '39.7837304',
              name: 'latitude',
              parent: `${parent}.country`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countryLongitude',
              example: '-100.4458825',
              name: 'longitude',
              parent: `${parent}.country`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countryName',
              example: 'United States of America',
              name: 'name',
              parent: `${parent}.country`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countryNameEs',
              example: 'Estados Unidos',
              name: 'nameEs',
              parent: `${parent}.country`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countryNameFr',
              example: 'États-Unis',
              name: 'nameFr',
              parent: `${parent}.country`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.county',
          name: 'county',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.countyCode',
              example: 'chester-county|us',
              name: 'code',
              parent: `${parent}.county`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countyLatitude',
              example: '47.6038321',
              name: 'latitude',
              parent: `${parent}.county`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countyLongitude',
              example: '-122.3300624',
              name: 'longitude',
              parent: `${parent}.county`,
              type: 'string',
            },
            {
              description: 'api.company.v1.countyName',
              example: 'Chester County',
              name: 'name',
              parent: `${parent}.county`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.description',
          example: 'Amazon.com, Inc. is an American multinational technology company based in Seattle, Washington, which focuses on…',
          name: 'description',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.descriptionShort',
          example: 'Amazon.com, Inc. is an American multinational technology company based in Seattle.',
          name: 'descriptionShort',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.domain',
          example: 'amazon.com',
          name: 'domain',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.domainAlts',
          name: 'domainAlts',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.domainName',
          example: 'amazon',
          name: 'domainName',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.domainTld',
          example: 'com',
          name: 'domainTld',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.emailPatterns',
          name: 'emailPatterns',
          parent: `${parent}`,
          type: 'array',

          attributes: [
            {
              description: 'api.company.v1.emailPatternsPattern',
              name: 'pattern',
              parent: `${parent}.emailPatterns`,
              type: 'string',
            },
            {
              description: 'api.company.v1.emailPatternsUsagePercentage',
              name: 'usagePercentage',
              parent: `${parent}.emailPatterns`,
              type: 'number',
            },
          ],
        },
        {
          description: 'api.company.v1.id',
          example: 5130216,
          name: 'id',
          parent: `${parent}`,
          type: 'number',
        },
        {
          description: 'api.company.v1.industries',
          example: [
            'internet',
            'fashion',
            'apparel',
          ],
          name: 'industries',
          parent: `${parent}`,
          type: 'array',
        },
        {
          description: 'api.company.v1.industryMain',
          example: 'Internet',
          name: 'industryMain',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.logo',
          example: 'https://thecompaniesapi.s3.fr-par.scw.cloud/images/domains/amazon.com.jpg',
          name: 'logo',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.monthlyVisitors',
          example: 'over-1b',
          name: 'monthlyVisitors',
          parent: `${parent}`,
          type: 'string',
          values: [
            'under-10k',
            '10k-50k',
            '50k-100k',
            '100k-500k',
            '500k-1m',
            '1m-10m',
            '10m-50m',
            '50m-100m',
            '100m-500m',
            '500m-1b',
            'over-1b',
          ],
        },
        {
          description: 'api.company.v1.name',
          example: 'Amazon',
          name: 'name',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.phoneNumber',
          example: '00 1 206-922-0880',
          name: 'phoneNumber',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.revenue',
          example: 'over-1b',
          name: 'revenue',
          parent: `${parent}`,
          type: 'string',
          values: [
            'under-1m',
            '1m-10m',
            '10m-50m',
            '50m-100m',
            '100m-200m',
            '200m-1b',
            'over-1b',
          ],
        },
      )

      if (specialFields.includes('similarAttributes')) {
        attributes.push({
          description: 'api.company.v1.similarAttributes',
          name: 'similarAttributes',
          parent: `${parent}`,
          type: 'array',

          attributes: [
            {
              description: 'api.company.v1.similarAttributesAttribute',
              example: 'industry',
              name: 'attribute',
              parent: `${parent}.similarAttributes`,
              type: 'string',
            },
            {
              description: 'api.company.v1.similarAttributesValue',
              example: 'fashion',
              name: 'value',
              parent: `${parent}.similarAttributes`,
              type: 'string',
            },
          ],
        })
      }

      if (specialFields.includes('similarDomains')) {
        attributes.push({
          description: 'api.company.v1.similarDomains',
          example: ['amazon.com'],
          name: 'similarDomains',
          parent: `${parent}`,
          type: 'array',
        })
      }

      if (specialFields.includes('similitude')) {
        attributes.push({
          description: 'api.company.v1.similitude',
          example: 95.38,
          name: 'similitude',
          parent: `${parent}`,
          type: 'number',
        })
      }

      attributes.push(
        {
          description: 'api.company.v1.socialNetworks',
          name: 'socialNetworks',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.socialNetworksDribbble',
              name: 'dribbble',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksDribbbleId',
              name: 'dribbbleId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksFacebook',
              name: 'facebook',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksFacebookId',
              name: 'facebookId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksGithub',
              name: 'github',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksGithubId',
              name: 'githubId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksInstagram',
              name: 'instagram',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksInstagramId',
              name: 'instagramId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksLinkedin',
              name: 'linkedin',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksLinkedinIdAlpha',
              name: 'linkedinIdAlpha',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksLinkedinIdNumeric',
              name: 'linkedinIdNumeric',
              parent: `${parent}.socialNetworks`,
              type: 'number',
            },
            {
              description: 'api.company.v1.socialNetworksLinkedinSalesNavigator',
              name: 'linkedinSalesNavigator',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksPinterest',
              name: 'pinterest',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksPinterestId',
              name: 'pinterestId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksSnapchat',
              name: 'snapchat',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksSnapchatId',
              name: 'snapchatId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksTiktok',
              name: 'tiktok',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksTiktokId',
              name: 'tiktokId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksTwitter',
              name: 'twitter',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksTwitterId',
              name: 'twitterId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksWellfound',
              name: 'wellfound',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksWellfoundId',
              name: 'wellfoundId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksYoutube',
              name: 'youtube',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
            {
              description: 'api.company.v1.socialNetworksYoutubeId',
              name: 'youtubeId',
              parent: `${parent}.socialNetworks`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.state',
          name: 'state',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v1.stateCode',
              example: 'pennsylvania|us',
              name: 'code',
              parent: `${parent}.state`,
              type: 'string',
            },
            {
              description: 'api.company.v1.stateLatitude',
              example: '40.9699889',
              name: 'latitude',
              parent: `${parent}.state`,
              type: 'string',
            },
            {
              description: 'api.company.v1.stateLongitude',
              example: '-77.7278831',
              name: 'longitude',
              parent: `${parent}.state`,
              type: 'string',
            },
            {
              description: 'api.company.v1.stateName',
              example: 'Pennsylvania',
              name: 'name',
              parent: `${parent}.state`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v1.stockExchange',
          example: 'NYSE',
          name: 'stockExchange',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.stockSymbol',
          example: 'AMZN',
          name: 'stockSymbol',
          parent: `${parent}`,
          type: 'string',
        },
        {
          description: 'api.company.v1.technologies',
          name: 'technologies',
          parent: `${parent}`,
          type: 'array',
        },
        {
          description: 'api.company.v1.technologyCategories',
          name: 'technologyCategories',
          parent: `${parent}`,
          type: 'array',
        },
        {
          description: 'api.company.v1.totalEmployees',
          example: 'over-10k',
          name: 'totalEmployees',
          parent: `${parent}`,
          type: 'string',
          values: [
            '1-10',
            '10-50',
            '50-200',
            '200-500',
            '500-1k',
            '1k-5k',
            '5k-10k',
            'over-10k',
          ],
        },
        {
          description: 'api.company.v1.totalEmployeesExact',
          example: 3930,
          name: 'totalEmployeesExact',
          parent: `${parent}`,
          type: 'number',
        },
        {
          description: 'api.company.v1.yearFounded',
          example: 1994,
          name: 'yearFounded',
          parent: `${parent}`,
          type: 'number',
        },
      )
    }
    else if (params.version === 'v2') {
      attributes.push(
        {
          description: 'api.company.v2.about._',
          name: 'about',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.about.businessType',
              example: 'privately-held',
              name: 'businessType',
              parent: `${parent}.about`,
              type: 'string',
              values: [
                'educational-institution',
                'government-agency',
                'nonprofit',
                'partnership',
                'privately-held',
                'public-company',
                'self-employed',
                'sole-proprietorship',
              ],
            },
            {
              description: 'api.company.v2.about.industries',
              example: [
                'hospitality',
                'entertainment',
                'real-estate',
                'import-export',
              ],
              name: 'industries',
              parent: `${parent}.about`,
              type: 'array',
            },
            {
              description: 'api.company.v2.about.industry',
              example: 'hospitality',
              link: {
                label: 'api.datasets.downloadAllIndustries',
                to: 'https://thecompaniesapi.s3.fr-par.scw.cloud/datasets/industries.json',
              },
              name: 'industry',
              parent: `${parent}.about`,
              simplified: true,
              type: 'string',
            },
            {
              description: 'api.company.v2.about.languages',
              example: [
                'en',
                'es',
                'fr',
              ],
              name: 'languages',
              parent: `${parent}.about`,
              type: 'array',
            },
            {
              description: 'api.company.v2.about.name',
              example: 'Jay Gatsby Enterprises',
              name: 'name',
              parent: `${parent}.about`,
              simplified: true,
              type: 'string',
            },
            {
              description: 'api.company.v2.about.nameAlts',
              example: ['Gatsby Inc.', 'Gatsby Holdings'],
              name: 'nameAlts',
              parent: `${parent}.about`,
              type: 'array',
            },
            {
              description: 'api.company.v2.about.nameLegal',
              example: 'Jay Gatsby Enterprises LLC',
              name: 'nameLegal',
              parent: `${parent}.about`,
              type: 'string',
            },
            {
              description: 'api.company.v2.about.totalEmployees',
              example: '200-500',
              name: 'totalEmployees',
              parent: `${parent}.about`,
              type: 'string',
              values: [
                '1-10',
                '10-50',
                '50-200',
                '200-500',
                '500-1k',
                '1k-5k',
                '5k-10k',
                'over-10k',
              ],
            },
            {
              description: 'api.company.v2.about.totalEmployeesExact',
              example: 435,
              name: 'totalEmployeesExact',
              parent: `${parent}.about`,
              type: 'number',
            },
            {
              description: 'api.company.v2.about.yearFounded',
              example: 1922,
              name: 'yearFounded',
              parent: `${parent}.about`,
              type: 'number',
            },
            {
              description: 'api.company.v2.about.yearFoundedDate',
              example: '1922-04-10',
              name: 'yearFoundedDate',
              parent: `${parent}.about`,
              type: 'string',
            },
            {
              description: 'api.company.v2.about.yearFoundedPlace',
              example: 'West Egg, Long Island, New York, US',
              name: 'yearFoundedPlace',
              parent: `${parent}.about`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v2.analytics._',
          name: 'analytics',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.analytics.lighthouse._',
              name: 'lighthouse',
              parent: `${parent}.analytics`,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v2.analytics.lighthouse.accessibility',
                  example: 95,
                  name: 'accessibility',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.bestPractices',
                  example: 90,
                  name: 'bestPractices',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.cumulativeLayoutShift._',
                  name: 'cumulativeLayoutShift',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.analytics.lighthouse.cumulativeLayoutShift.count',
                      example: 3,
                      name: 'count',
                      parent: `${parent}.analytics.lighthouse.cumulativeLayoutShift`,
                      type: 'number',
                    },
                    {
                      description: 'api.company.v2.analytics.lighthouse.cumulativeLayoutShift.score',
                      example: 0.1,
                      name: 'score',
                      parent: `${parent}.analytics.lighthouse.cumulativeLayoutShift`,
                      type: 'number',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.largestContentfulPaint._',
                  name: 'largestContentfulPaint',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.analytics.lighthouse.largestContentfulPaint.ms',
                      example: 2500,
                      name: 'ms',
                      parent: `${parent}.analytics.lighthouse.largestContentfulPaint`,
                      type: 'number',
                    },
                    {
                      description: 'api.company.v2.analytics.lighthouse.largestContentfulPaint.score',
                      example: 0.9,
                      name: 'score',
                      parent: `${parent}.analytics.lighthouse.largestContentfulPaint`,
                      type: 'number',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.maxServerLatency',
                  example: 120,
                  name: 'maxServerLatency',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.numFonts',
                  example: 5,
                  name: 'numFonts',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.numRequests',
                  example: 80,
                  name: 'numRequests',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.numScripts',
                  example: 20,
                  name: 'numScripts',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.numStylesheets',
                  example: 10,
                  name: 'numStylesheets',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.numTasks',
                  example: 50,
                  name: 'numTasks',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.performance',
                  example: 85,
                  name: 'performance',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.seo',
                  example: 92,
                  name: 'seo',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.timeToInteractive._',
                  name: 'timeToInteractive',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.analytics.lighthouse.timeToInteractive.ms',
                      example: 3000,
                      name: 'ms',
                      parent: `${parent}.analytics.lighthouse.timeToInteractive`,
                      type: 'number',
                    },
                    {
                      description: 'api.company.v2.analytics.lighthouse.timeToInteractive.score',
                      example: 0.8,
                      name: 'score',
                      parent: `${parent}.analytics.lighthouse.timeToInteractive`,
                      type: 'number',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.analytics.lighthouse.totalBlockingTime._',
                  name: 'totalBlockingTime',
                  parent: `${parent}.analytics.lighthouse`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.analytics.lighthouse.totalBlockingTime.ms',
                      example: 200,
                      name: 'ms',
                      parent: `${parent}.analytics.lighthouse.totalBlockingTime`,
                      type: 'number',
                    },
                    {
                      description: 'api.company.v2.analytics.lighthouse.totalBlockingTime.score',
                      example: 0.85,
                      name: 'score',
                      parent: `${parent}.analytics.lighthouse.totalBlockingTime`,
                      type: 'number',
                    },
                  ],
                },
              ],
            },
            {
              description: 'api.company.v2.analytics.monthlyVisitors',
              example: '500k-1m',
              name: 'monthlyVisitors',
              parent: `${parent}.analytics`,
              type: 'string',
              values: [
                'under-10k',
                '10k-50k',
                '50k-100k',
                '100k-500k',
                '500k-1m',
                '1m-10m',
                '10m-50m',
                '50m-100m',
                '100m-500m',
                '500m-1b',
                'over-1b',
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.apps._',
          name: 'apps',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '1',
                  name: 'Gatsby Hospitality App',
                  rating: 4.8,
                  reviews: 200,
                  type: 'app',
                  url: 'https://www.amazon.com/gp/product/B08XYZ',
                },
                parent: `${parent}.apps.amazon`,
              }),
              description: 'api.company.v2.apps.amazon',
              name: 'amazon',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '2',
                  name: 'Gatsby Real Estate',
                  rating: 4.5,
                  reviews: 150,
                  type: 'app',
                  url: 'https://play.google.com/store/apps/details?id=com.gatsby.realestate',
                },
                parent: `${parent}.apps.android`,
              }),
              description: 'api.company.v2.apps.android',
              name: 'android',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '3',
                  name: 'Gatsby Import-Export Extension',
                  rating: 4.3,
                  reviews: 380,
                  type: 'extension',
                  url: 'https://chrome.google.com/webstore/detail/gatsby-import-export/pkhjlm',
                },
                parent: `${parent}.apps.chrome`,
              }),
              description: 'api.company.v2.apps.chrome',
              name: 'chrome',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '4',
                  name: 'Gatsby Entertainment',
                  rating: 4.7,
                  reviews: 250,
                  type: 'app',
                  url: 'https://apps.apple.com/us/app/gatsby-entertainment/id123456789',
                },
                parent: `${parent}.apps.ios`,
              }),
              description: 'api.company.v2.apps.ios',
              name: 'ios',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '5',
                  name: 'Gatsby Enterprises Mac App',
                  rating: 4.6,
                  reviews: 180,
                  type: 'app',
                  url: 'https://apps.apple.com/us/app/gatsby-enterprises/id987654321',
                },
                parent: `${parent}.apps.mac`,
              }),
              description: 'api.company.v2.apps.mac',
              name: 'mac',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '6',
                  name: 'Gatsby Meta App',
                  rating: 4.4,
                  reviews: 120,
                  type: 'app',
                  url: 'https://www.meta.com/gatsby',
                },
                parent: `${parent}.apps.meta`,
              }),
              description: 'api.company.v2.apps.meta',
              name: 'meta',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '7',
                  name: 'Gatsby Business Solutions',
                  rating: 4.5,
                  reviews: 130,
                  type: 'app',
                  url: 'https://www.microsoft.com/store/apps/9nblggh12345',
                },
                parent: `${parent}.apps.microsoft`,
              }),
              description: 'api.company.v2.apps.microsoft',
              name: 'microsoft',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '8',
                  name: 'Gatsby Gaming',
                  rating: 4.6,
                  reviews: 140,
                  type: 'app',
                  url: 'https://store.playstation.com/en-us/product/UP1234-CUSA56789_00-GATSBYGAMINGAPP',
                },
                parent: `${parent}.apps.playstation`,
              }),
              description: 'api.company.v2.apps.playstation',
              name: 'playstation',
              parent: `${parent}.apps`,
              type: 'array',
            },
            {
              attributes: getCompanyAppAttributes({
                example: {
                  id: '9',
                  name: 'Gatsby Xbox App',
                  rating: 4.7,
                  reviews: 160,
                  type: 'app',
                  url: 'https://www.microsoft.com/en-us/p/gatsby-xbox-app/9nblggh56789',
                },
                parent: `${parent}.apps.xbox`,
              }),
              description: 'api.company.v2.apps.xbox',
              name: 'xbox',
              parent: `${parent}.apps`,
              type: 'array',
            },
          ],
        },
        {
          description: 'api.company.v2.assets._',
          name: 'assets',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.assets.colorPrimary',
              example: '#0d111f',
              name: 'colorPrimary',
              parent: `${parent}.assets`,
              type: 'string',
            },
            {
              description: 'api.company.v2.assets.cover._',
              name: 'cover',
              parent: `${parent}.assets`,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v2.assets.cover.height',
                  example: 1500,
                  name: 'height',
                  parent: `${parent}.assets.cover`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.assets.cover.src',
                  example: 'https://assets.thecompaniesapi.com/companies/covers/jaygatsbyenterprises.com.jpg',
                  name: 'src',
                  parent: `${parent}.assets.cover`,
                  type: 'string',
                },
                {
                  description: 'api.company.v2.assets.cover.width',
                  example: 500,
                  name: 'width',
                  parent: `${parent}.assets.cover`,
                  type: 'number',
                },
              ],
            },
            {
              description: 'api.company.v2.assets.logoSquare._',
              name: 'logoSquare',
              parent: `${parent}.assets`,
              simplified: true,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v2.assets.logoSquare.height',
                  example: 200,
                  name: 'height',
                  parent: `${parent}.assets.logoSquare`,
                  type: 'number',
                },
                {
                  description: 'api.company.v2.assets.logoSquare.src',
                  example: 'https://assets.thecompaniesapi.com/companies/logos/square/jaygatsbyenterprises.com.jpg',
                  name: 'src',
                  parent: `${parent}.assets.logoSquare`,
                  type: 'string',
                },
                {
                  description: 'api.company.v2.assets.logoSquare.width',
                  example: 200,
                  name: 'width',
                  parent: `${parent}.assets.logoSquare`,
                  type: 'number',
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.codes._',
          name: 'codes',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.codes.naics',
              example: ['721110'],
              name: 'naics',
              parent: `${parent}.codes`,
              type: 'array',
            },
            {
              description: 'api.company.v2.codes.sic',
              example: ['7011'],
              name: 'sic',
              parent: `${parent}.codes`,
              type: 'array',
            },
          ],
        },
        {
          description: 'api.company.v2.companies._',
          name: 'companies',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              attributes: getCompanyCompaniesAttributes({
                example: {
                  description: 'Entertainment & VR Hospitality for all',
                  domain: 'gatsbyentertainment.com',
                  name: 'Gatsby Entertainment',
                },
                parent: `${parent}.companies.acquisitions`,
              }),
              description: 'api.company.v2.companies.acquisitions',
              name: 'acquisitions',
              parent: `${parent}.companies`,
              type: 'array',
            },
            {
              attributes: getCompanyCompaniesAttributes({
                example: {
                  description: 'The holding company for all Gatsby subsidiaries.',
                  domain: 'gatsbyholdings.com',
                  name: 'Gatsby Holdings',
                },
                parent: `${parent}.companies.parent`,
              }),
              description: 'api.company.v2.companies.parent',
              name: 'parent',
              parent: `${parent}.companies`,
              type: 'object',
            },
            {
              attributes: getCompanyCompaniesAttributes({
                example: {
                  description: 'Invest in your future with Gatsby Real Estate.',
                  domain: 'gatsbyrealestate.com',
                  name: 'Gatsby Real Estate',
                },
                parent: `${parent}.companies.subsidiaries`,
              }),
              description: 'api.company.v2.companies.subsidiaries',
              name: 'subsidiaries',
              parent: `${parent}.companies`,
              type: 'array',
            },
          ],
        },
        {
          description: 'api.company.v2.contacts._',
          name: 'contacts',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              attributes: getCompanyContactAttributes({
                example: {
                  category: 'general',
                  value: 'contact@jaygatsbyenterprises.com',
                },
                parent: `${parent}.contacts.emails`,
              }),
              description: 'api.company.v2.contacts.emails',
              exampleToOverride: [
                {
                  category: 'general',
                  value: 'contact@jaygatsbyenterprises.com',
                },
                {
                  category: 'technical',
                  value: 'technical-support@jaygatsbyenterprises.com',
                },
              ],
              name: 'emails',
              parent: `${parent}.contacts`,
              type: 'array',
            },
            {
              attributes: getCompanyContactAttributes({
                example: {
                  category: 'general',
                  value: 'http://line.me/ti/p/~jaygatsbyenterprises',
                },
                parent: `${parent}.contacts.lines`,
              }),
              description: 'api.company.v2.contacts.lines',
              name: 'lines',
              parent: `${parent}.contacts`,
              type: 'array',
            },
            {
              attributes: getCompanyContactAttributes({
                example: {
                  category: 'general',
                  value: '+62-821-8750-5095',
                },
                parent: `${parent}.contacts.phones`,
              }),
              description: 'api.company.v2.contacts.phones',
              name: 'phones',
              parent: `${parent}.contacts`,
              type: 'array',
            },
            {
              attributes: getCompanyContactAttributes({
                example: {
                  category: 'general',
                  value: 'https://wa.me/27116783406',
                },
                parent: `${parent}.contacts.socials`,
              }),
              description: 'api.company.v2.contacts.whatsapps',
              name: 'whatsapps',
              parent: `${parent}.contacts`,
              type: 'array',
            },
          ],
        },
        {
          description: 'api.company.v2.contents._',
          name: 'contents',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.contents.podcasts._',
              name: 'podcasts',
              parent: `${parent}.contents`,
              type: 'object',

              attributes: [
                {
                  attributes: getCompanyPodcastAttributes({
                    examples: {
                      id: '123456789',
                      name: 'Gatsby Insights',
                      rating: 4.9,
                      reviews: 300,
                      url: 'https://podcasts.apple.com/us/podcast/gatsby-insights/id123456789',
                    },
                    parent: `${parent}.contents.podcasts.apple`,
                  }),
                  description: 'api.company.v2.contents.podcasts.apple',
                  name: 'apple',
                  parent: `${parent}.contents.podcasts`,
                  type: 'array',
                },
                {
                  attributes: getCompanyPodcastAttributes({
                    examples: {
                      id: '123456789',
                      name: 'Gatsby Talks',
                      rating: 4.8,
                      reviews: 250,
                      url: 'https://open.spotify.com/show/123456789',
                    },
                    parent: `${parent}.contents.podcasts.spotify`,
                  }),
                  description: 'api.company.v2.contents.podcasts.spotify',
                  name: 'spotify',
                  parent: `${parent}.contents.podcasts`,
                  type: 'array',
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.descriptions._',
          name: 'descriptions',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.descriptions.knowledgeGraph',
              example: 'Jay Gatsby Enterprises is a premier company known for its luxury offerings in hospitality, entertainment, real estate, and import-export industries.',
              name: 'knowledgeGraph',
              parent: `${parent}.descriptions`,
              type: 'string',
            },
            {
              description: 'api.company.v2.descriptions.linkedin',
              example: 'Jay Gatsby Enterprises is an industry leader specializing in luxury hospitality, entertainment, real estate, and import-export services, committed to delivering unparalleled experiences and excellence.',
              name: 'linkedin',
              parent: `${parent}.descriptions`,
              type: 'string',
            },
            {
              description: 'api.company.v2.descriptions.primary',
              example: 'Jay Gatsby Enterprises is a leading provider of luxury services and products in various industries.',
              name: 'primary',
              parent: `${parent}.descriptions`,
              type: 'string',
            },
            {
              description: 'api.company.v2.descriptions.tagline',
              example: 'Experience the luxury lifestyle.',
              name: 'tagline',
              parent: `${parent}.descriptions`,
              simplified: true,
              type: 'string',
            },
            {
              description: 'api.company.v2.descriptions.website',
              example: 'Jay Gatsby Enterprises is a leading company recognized for its high-end services in the fields of hospitality, entertainment, real estate, and import-export.',
              name: 'website',
              parent: `${parent}.descriptions`,
              type: 'string',
            },
            {
              description: 'api.company.v2.descriptions.wikipedia',
              example: 'Jay Gatsby Enterprises stands out as a top-tier firm renowned for its upscale offerings across the hospitality, entertainment, real estate, and import-export sectors.',
              name: 'wikipedia',
              parent: `${parent}.descriptions`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v2.domain._',
          name: 'domain',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.domain.createdAt',
              example: '1922-04-10',
              name: 'createdAt',
              parent: `${parent}.domain`,
              simplified: true,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.domain',
              example: 'jaygatsbyenterprises.com',
              name: 'domain',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.domainAlts',
              example: ['gatsbyinc.com', 'gatsbyholdings.com'],
              name: 'domainAlts',
              parent: `${parent}.domain`,
              type: 'array',
            },
            {
              description: 'api.company.v2.domain.domainName',
              example: 'jaygatsbyenterprises',
              name: 'domainName',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.expiredAt',
              example: '2033-04-10',
              name: 'expiredAt',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.nsfw',
              example: true,
              name: 'nsfw',
              parent: `${parent}.domain`,
              type: 'boolean',
            },
            {
              description: 'api.company.v2.domain.redirection',
              example: 'www.gatsbyholdings.com',
              name: 'redirection',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.registrantCountry',
              example: 'us',
              name: 'registrantCountry',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.registrantPhone',
              example: '+1-800-123-4567',
              name: 'registrantPhone',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.registrar',
              example: 'Domain Registrar Inc.',
              name: 'registrar',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.registrarUrl',
              example: 'https://www.domainregistrar.com',
              name: 'registrarUrl',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.state',
              example: 'operating',
              name: 'state',
              parent: `${parent}.domain`,
              type: 'string',
              values: [
                'broken',
                'for-sale',
                'operating',
                'redirected',
              ],
            },
            {
              description: 'api.company.v2.domain.status',
              example: 200,
              name: 'status',
              parent: `${parent}.domain`,
              type: 'number',
            },
            {
              description: 'api.company.v2.domain.tld',
              example: 'com',
              name: 'tld',
              parent: `${parent}.domain`,
              type: 'string',
            },
            {
              description: 'api.company.v2.domain.updatedAt',
              example: '2023-04-10',
              name: 'updatedAt',
              parent: `${parent}.domain`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v2.finances._',
          name: 'finances',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.finances.revenue',
              example: '100m-200m',
              name: 'revenue',
              parent: `${parent}.finances`,
              type: 'string',
              values: [
                'under-1m',
                '1m-10m',
                '10m-50m',
                '50m-100m',
                '100m-200m',
                '200m-1b',
                'over-1b',
              ],
            },
            {
              description: 'api.company.v2.finances.stockExchange',
              example: 'NYSE',
              name: 'stockExchange',
              parent: `${parent}.finances`,
              type: 'string',
            },
            {
              description: 'api.company.v2.finances.stockSymbol',
              example: 'GATSBY',
              name: 'stockSymbol',
              parent: `${parent}.finances`,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v2.locations._',
          name: 'locations',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.locations.headquarters._',
              name: 'headquarters',
              parent: `${parent}.locations`,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v2.locations.headquarters.address._',
                  name: 'address',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 40.7391,
                          lon: -73.9922,
                        },
                        parent: `${parent}.locations.headquarters.address`,
                      }),
                      description: 'api.company.v2.locations.headquarters.address.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.address`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.address.raw',
                      example: '123 Gatsby Lane, West Egg, Long Island, NY, 11050, US',
                      name: 'raw',
                      parent: `${parent}.locations.headquarters.address`,
                      type: 'string',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.locations.headquarters.city._',
                  name: 'city',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.locations.headquarters.city.code',
                      example: 'new-york|new-york|us',
                      name: 'code',
                      parent: `${parent}.locations.headquarters.city`,
                      type: 'string',
                    },
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 40.7127281,
                          lon: -74.0060152,
                        },
                        parent: `${parent}.locations.headquarters.city`,
                      }),
                      description: 'api.company.v2.locations.headquarters.city.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.city`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.city.name',
                      example: 'New York',
                      name: 'name',
                      parent: `${parent}.locations.headquarters.city`,
                      type: 'string',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.city.postcode',
                      example: '11050',
                      name: 'postcode',
                      parent: `${parent}.locations.headquarters.city`,
                      type: 'string',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.locations.headquarters.continent._',
                  name: 'continent',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.locations.headquarters.continent.code',
                      example: 'na',
                      name: 'code',
                      parent: `${parent}.locations.headquarters.continent`,
                      type: 'string',
                    },
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 51.0000002,
                          lon: -109,
                        },
                        parent: `${parent}.locations.headquarters.continent`,
                      }),
                      description: 'api.company.v2.locations.headquarters.continent.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.continent`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.continent.name',
                      example: 'North America',
                      name: 'name',
                      parent: `${parent}.locations.headquarters.continent`,
                      type: 'string',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.locations.headquarters.country._',
                  link: {
                    label: 'api.datasets.downloadAllCountries',
                    to: 'https://thecompaniesapi.s3.fr-par.scw.cloud/datasets/countries.json',
                  },
                  name: 'country',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.locations.headquarters.country.code',
                      example: 'us',
                      name: 'code',
                      parent: `${parent}.locations.headquarters.country`,
                      type: 'string',
                    },
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 39.7837304,
                          lon: -100.4458825,
                        },
                        parent: `${parent}.locations.headquarters.country`,
                      }),
                      description: 'api.company.v2.locations.headquarters.country.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.country`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.country.name',
                      example: 'United States',
                      name: 'name',
                      parent: `${parent}.locations.headquarters.country`,
                      type: 'string',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.locations.headquarters.county._',
                  name: 'county',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.locations.headquarters.county.code',
                      example: 'nassau-county|us',
                      name: 'code',
                      parent: `${parent}.locations.headquarters.county`,
                      type: 'string',
                    },
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 40.74358475,
                          lon: -73.56890580233586,
                        },
                        parent: `${parent}.locations.headquarters.county`,
                      }),
                      description: 'api.company.v2.locations.headquarters.county.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.county`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.county.name',
                      example: 'Nassau County',
                      name: 'name',
                      parent: `${parent}.locations.headquarters.county`,
                      type: 'string',
                    },
                  ],
                },
                {
                  description: 'api.company.v2.locations.headquarters.state._',
                  name: 'state',
                  parent: `${parent}.locations.headquarters`,
                  type: 'object',

                  attributes: [
                    {
                      description: 'api.company.v2.locations.headquarters.state.code',
                      example: 'new-york|us',
                      name: 'code',
                      parent: `${parent}.locations.headquarters.state`,
                      type: 'string',
                    },
                    {
                      attributes: getCompanyLocationsGeopointAttributes({
                        example: {
                          lat: 40.7127281,
                          lon: -74.0060152,
                        },
                        parent: `${parent}.locations.headquarters.state`,
                      }),
                      description: 'api.company.v2.locations.headquarters.state.geopoint',
                      name: 'geopoint',
                      parent: `${parent}.locations.headquarters.state`,
                      type: 'object',
                    },
                    {
                      description: 'api.company.v2.locations.headquarters.state.name',
                      example: 'New York',
                      name: 'name',
                      parent: `${parent}.locations.headquarters.state`,
                      type: 'string',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.meta._',
          name: 'meta',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.meta.listIds',
              example: [123456789, 987654321],
              name: 'listIds',
              parent: `${parent}.meta`,
              type: 'array',
            },
            {
              description: 'api.company.v2.meta.mysqlId',
              example: 123456789,
              name: 'mysqlId',
              parent: `${parent}.meta`,
              type: 'number',
            },
            {
              description: 'api.company.v2.meta.score',
              example: 95,
              name: 'score',
              parent: `${parent}.meta`,
              type: 'number',
            },
            {
              description: 'api.company.v2.meta.syncedAt',
              example: '2023-01-01T00:00:00Z',
              name: 'syncedAt',
              parent: `${parent}.meta`,
              simplified: true,
              type: 'string',
            },
          ],
        },
        {
          description: 'api.company.v2.secondaries._',
          name: 'secondaries',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.secondaries.emailPatterns._',
              name: 'emailPatterns',
              parent: `${parent}.secondaries`,
              type: 'array',

              attributes: [
                {
                  description: 'api.company.v2.secondaries.emailPatterns.pattern',
                  example: '[F].[L]',
                  name: 'pattern',
                  parent: `${parent}.secondaries.emailPatterns`,
                  type: 'string',
                },
                {
                  description: 'api.company.v2.secondaries.emailPatterns.usagePercentage',
                  example: 98.4069,
                  name: 'usagePercentage',
                  parent: `${parent}.secondaries.emailPatterns`,
                  type: 'number',
                },
              ],
              exampleToOverride: [
                {
                  pattern: '[F].[L]',
                  usagePercentage: 98.4069,
                },
                {
                  pattern: '[F1][L]',
                  usagePercentage: 0.674,
                },
                {
                  pattern: '[L].[F]',
                  usagePercentage: 0.6127,
                },
                {
                  pattern: '[L]',
                  usagePercentage: 0.1225,
                },
                {
                  pattern: '[F][L]',
                  usagePercentage: 0.1225,
                },
                {
                  pattern: '[F1].[L]',
                  usagePercentage: 0.0613,
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.socials._',
          name: 'socials',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members'],
                example: {
                  id: 'jay-gatsby-club',
                  members: 1500,
                  url: 'https://discord.gg/jay-gatsby-club',
                },
                parent: `${parent}.socials.discord`,
              }),
              description: 'api.company.v2.socials.discord',
              name: 'discord',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                example: {
                  id: 'jaygatsbyenterprises',
                  url: 'https://dribbble.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.dribbble`,
              }),
              description: 'api.company.v2.socials.dribbble',
              name: 'dribbble',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'likes',
                  'talkingAbout',
                  'wereHere',
                ],
                example: {
                  followers: 50000,
                  id: 'jaygatsbyenterprises',
                  likes: 45000,
                  talkingAbout: 1000,
                  url: 'https://www.facebook.com/jaygatsbyenterprises',
                  wereHere: 5000,
                },
                parent: `${parent}.socials.facebook`,
              }),
              description: 'api.company.v2.socials.facebook',
              name: 'facebook',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members'],
                example: {
                  id: 'jaygatsbyenterprises-vip',
                  members: 10000,
                  url: 'https://www.facebook.com/groups/jaygatsbyenterprises-vip',
                },
                parent: `${parent}.socials.facebookGroup`,
              }),
              description: 'api.company.v2.socials.facebookGroup',
              name: 'facebookGroup',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers', 'members'],
                example: {
                  followers: 2000,
                  id: 'jaygatsbyenterprises',
                  members: 50,
                  url: 'https://github.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.github`,
              }),
              description: 'api.company.v2.socials.github',
              name: 'github',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'following',
                  'posts',
                ],
                example: {
                  followers: 30000,
                  following: 100,
                  id: 'jaygatsbyenterprises',
                  posts: 200,
                  url: 'https://www.instagram.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.instagram`,
              }),
              description: 'api.company.v2.socials.instagram',
              name: 'instagram',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'idNumeric',
                ],
                example: {
                  followers: 40000,
                  id: 'jaygatsbyenterprises',
                  idNumeric: '123456789',
                  url: 'https://www.linkedin.com/company/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.linkedin`,
              }),
              description: 'api.company.v2.socials.linkedin',
              name: 'linkedin',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'following',
                  'posts',
                ],
                example: {
                  followers: 5000,
                  following: 50,
                  id: 'jaygatsbyenterprises',
                  posts: 100,
                  url: 'https://mastodon.social/@jaygatsbyenterprises',
                },
                parent: `${parent}.socials.mastodon`,
              }),
              description: 'api.company.v2.socials.mastodon',
              name: 'mastodon',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 15000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://medium.com/@jaygatsbyenterprises',
                },
                parent: `${parent}.socials.medium`,
              }),
              description: 'api.company.v2.socials.medium',
              name: 'medium',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members', 'posts'],
                example: {
                  id: 'jaygatsbyenterprises',
                  members: 1000,
                  posts: 50,
                  url: 'https://www.patreon.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.patreon`,
              }),
              description: 'api.company.v2.socials.patreon',
              name: 'patreon',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'following',
                  'monthlyViews',
                ],
                example: {
                  followers: 10000,
                  following: 50,
                  id: 'jaygatsbyenterprises',
                  monthlyViews: 20000,
                  url: 'https://www.pinterest.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.pinterest`,
              }),
              description: 'api.company.v2.socials.pinterest',
              name: 'pinterest',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members'],
                example: {
                  id: 'jaygatsbyenterprises',
                  members: 5000,
                  url: 'https://www.reddit.com/r/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.reddit`,
              }),
              description: 'api.company.v2.socials.reddit',
              name: 'reddit',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members'],
                example: {
                  id: 'jaygatsbyenterprises',
                  members: 800,
                  url: 'https://slack.jaygatsbyenterprises.com',
                },
                parent: `${parent}.socials.slack`,
              }),
              description: 'api.company.v2.socials.slack',
              name: 'slack',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 25000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://www.snapchat.com/add/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.snapchat`,
              }),
              description: 'api.company.v2.socials.snapchat',
              name: 'snapchat',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['posts'],
                example: {
                  id: 'jaygatsbyenterprises',
                  posts: 100,
                  url: 'https://stackoverflow.com/questions/tagged/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.stackoverflow`,
              }),
              description: 'api.company.v2.socials.stackoverflow',
              name: 'stackoverflow',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['members'],
                example: {
                  id: 'jaygatsbyenterprises',
                  members: 1200,
                  url: 'https://steamcommunity.com/groups/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.steam`,
              }),
              description: 'api.company.v2.socials.steam',
              name: 'steam',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 7000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://jaygatsbyenterprises.substack.com',
                },
                parent: `${parent}.socials.substack`,
              }),
              description: 'api.company.v2.socials.substack',
              name: 'substack',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 15000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://www.threads.net/@jaygatsbyenterprises',
                },
                parent: `${parent}.socials.threads`,
              }),
              description: 'api.company.v2.socials.threads',
              name: 'threads',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'following',
                  'likes',
                ],
                example: {
                  followers: 35000,
                  following: 50,
                  id: 'jaygatsbyenterprises',
                  likes: 100000,
                  url: 'https://www.tiktok.com/@jaygatsbyenterprises',
                },
                parent: `${parent}.socials.tiktok`,
              }),
              description: 'api.company.v2.socials.tiktok',
              name: 'tiktok',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                example: {
                  id: 'jaygatsbyenterprises',
                  url: 'https://jaygatsbyenterprises.tumblr.com',
                },
                parent: `${parent}.socials.tumblr`,
              }),
              description: 'api.company.v2.socials.tumblr',
              name: 'tumblr',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 20000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://www.twitch.tv/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.twitch`,
              }),
              description: 'api.company.v2.socials.twitch',
              name: 'twitch',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers', 'following'],
                example: {
                  followers: 45000,
                  following: 300,
                  id: 'jaygatsbyenterprises',
                  url: 'https://twitter.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.twitter`,
              }),
              description: 'api.company.v2.socials.twitter',
              name: 'twitter',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: [
                  'followers',
                  'following',
                  'videos',
                ],
                example: {
                  followers: 15000,
                  following: 50,
                  id: 'jaygatsbyenterprises',
                  url: 'https://vimeo.com/jaygatsbyenterprises',
                  videos: 100,
                },
                parent: `${parent}.socials.vimeo`,
              }),
              description: 'api.company.v2.socials.vimeo',
              name: 'vimeo',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 10000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://vk.com/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.vkontakte`,
              }),
              description: 'api.company.v2.socials.vkontakte',
              name: 'vkontakte',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers'],
                example: {
                  followers: 8000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://www.xing.com/company/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.xing`,
              }),
              description: 'api.company.v2.socials.xing',
              name: 'xing',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['rating', 'reviews'],
                example: {
                  id: 'jaygatsbyenterprises',
                  rating: 4.5,
                  reviews: 200,
                  url: 'https://www.yelp.com/biz/jaygatsbyenterprises',
                },
                parent: `${parent}.socials.yelp`,
              }),
              description: 'api.company.v2.socials.yelp',
              name: 'yelp',
              parent: `${parent}.socials`,
              type: 'object',
            },
            {
              attributes: getCompanySocialAttributes({
                attributes: ['followers', 'videos'],
                example: {
                  followers: 60000,
                  id: 'jaygatsbyenterprises',
                  url: 'https://www.youtube.com/jaygatsbyenterprises',
                  videos: 150,
                },
                parent: `${parent}.socials.youtube`,
              }),
              description: 'api.company.v2.socials.youtube',
              name: 'youtube',
              parent: `${parent}.socials`,
              type: 'object',
            },
          ],
        },
        {
          description: 'api.company.v2.technologies._',
          name: 'technologies',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.technologies.active',
              example: [
                'amazon-s3',
                'brevo',
                'node-js',
                'react',
                'shopify',
              ],
              link: {
                label: 'api.datasets.downloadAllTechnologies',
                to: 'https://thecompaniesapi.s3.fr-par.scw.cloud/datasets/technologies.json',
              },
              name: 'active',
              parent: `${parent}.technologies`,
              type: 'array',
            },
            {
              description: 'api.company.v2.technologies.categories',
              example: [
                'archive-storage',
                'commerce',
                'conversion-rate-optimization',
                'development',
                'e-commerce',
                'e-commerce-platforms',
                'email-deliverability',
                'email-template-builder',
                'email-testing',
                'it-infrastructure',
                'javascript-web-frameworks',
                'landing-page-builders',
                'marketing-automation',
                'marketing',
                'object-storage',
                'sms-marketing',
                'storage-management',
                'transactional-email',
                'web-frameworks',
              ],
              name: 'categories',
              parent: `${parent}.technologies`,
              type: 'array',
            },
            {
              description: 'api.company.v2.technologies.details._',
              name: 'details',
              parent: `${parent}.technologies`,
              type: 'object',

              attributes: [
                {
                  description: 'api.company.v2.technologies.details.categories',
                  example: ['javascript-web-frameworks'],
                  name: 'categories',
                  parent: `${parent}.technologies.details`,
                  type: 'array',
                },
                {
                  description: 'api.company.v2.technologies.details.detectedAt',
                  example: '2023-06-15T12:00:00Z',
                  name: 'detectedAt',
                  parent: `${parent}.technologies.details`,
                  type: 'string',
                },
                {
                  description: 'api.company.v2.technologies.details.detectionTypes._',
                  example: [
                    'scripts',
                    'tags',
                    'variables',
                  ],
                  name: 'detectionTypes',
                  parent: `${parent}.technologies.details`,
                  type: 'array',
                  values: [
                    {
                      label: 'cookies',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.cookies'),
                    },
                    {
                      label: 'dns',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.dns'),
                    },
                    {
                      label: 'evaluate',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.evaluate'),
                    },
                    {
                      label: 'evaluate-bundle',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.evaluateBundle'),
                    },
                    {
                      label: 'evaluate-version',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.evaluateVersion'),
                    },
                    {
                      label: 'headers',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.headers'),
                    },
                    {
                      label: 'metas',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.metas'),
                    },
                    {
                      label: 'scripts',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.scripts'),
                    },
                    {
                      label: 'tags',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.tags'),
                    },
                    {
                      label: 'urls',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.urls'),
                    },
                    {
                      label: 'variables',
                      tooltip: t('api.company.v2.technologies.details.detectionTypes.variables'),
                    },
                  ],
                },
                {
                  description: 'api.company.v2.technologies.details.slug',
                  example: 'react',
                  name: 'slug',
                  parent: `${parent}.technologies.details`,
                  type: 'string',
                },
                {
                  description: 'api.company.v2.technologies.details.version',
                  example: '18.2.0',
                  name: 'version',
                  parent: `${parent}.technologies.details`,
                  type: 'string',
                },
              ],
              exampleToOverride: [
                {
                  categories: [
                    'archive-storage',
                    'it-infrastructure',
                    'object-storage',
                    'storage-management',
                  ],
                  detectedAt: '2023-06-15T12:00:00Z',
                  detectionTypes: ['headers'],
                  slug: 'amazon-s3',
                },
                {
                  categories: [
                    'conversion-rate-optimization',
                    'email-deliverability',
                    'email-template-builder',
                    'email-testing',
                    'landing-page-builders',
                    'marketing',
                    'marketing-automation',
                    'sms-marketing',
                    'transactional-email',
                  ],
                  detectedAt: '2023-06-15T12:00:00Z',
                  detectionTypes: ['dns'],
                  slug: 'brevo',
                },
                {
                  categories: [
                    'development',
                    'javascript-web-frameworks',
                    'web-frameworks',
                  ],
                  detectedAt: '2023-06-15T12:00:00Z',
                  detectionTypes: [],
                  slug: 'node-js',
                  version: '20.4.0',
                },
                {
                  categories: ['javascript-web-frameworks'],
                  detectedAt: '2023-06-15T12:00:00Z',
                  detectionTypes: [
                    'scripts',
                    'tags',
                    'variables',
                  ],
                  slug: 'react',
                  version: '18.2.0',
                },
                {
                  categories: [
                    'commerce',
                    'e-commerce',
                    'e-commerce-platforms',
                  ],
                  detectedAt: '2023-06-15T12:00:00Z',
                  detectionTypes: ['headers', 'tags'],
                  slug: 'shopify',
                },
              ],
            },
          ],
        },
        {
          description: 'api.company.v2.urls._',
          name: 'urls',
          parent: `${parent}`,
          type: 'object',

          attributes: [
            {
              description: 'api.company.v2.urls.about',
              example: 'https://www.jaygatsbyenterprises.com/about',
              name: 'about',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.blog',
              example: 'https://blog.jaygatsbyenterprises.com',
              name: 'blog',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.careers',
              example: 'https://www.jaygatsbyenterprises.com/careers',
              name: 'careers',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.contact',
              example: 'https://www.jaygatsbyenterprises.com/contact',
              name: 'contact',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.crunchbase',
              example: 'https://www.crunchbase.com/organization/jaygatsbyenterprises',
              name: 'crunchbase',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.developers',
              example: 'https://developers.jaygatsbyenterprises.com',
              name: 'developers',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.docs',
              example: 'https://docs.jaygatsbyenterprises.com',
              name: 'docs',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.events',
              example: 'https://www.jaygatsbyenterprises.com/events',
              name: 'events',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.glassdoor',
              example: 'https://glassdoor.com/company/jaygatsbyenterprises',
              name: 'glassdoor',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.partnership',
              example: 'https://www.jaygatsbyenterprises.com/partnerships',
              name: 'partnership',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.products.pricing',
              example: 'https://www.jaygatsbyenterprises.com/pricing',
              name: 'pricing',
              parent: `${parent}.products`,
              type: 'string',
            },
            {
              description: 'api.company.v2.legals.privacy',
              example: 'https://www.jaygatsbyenterprises.com/privacy',
              name: 'privacy',
              parent: `${parent}.legals`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.salesNavigator',
              example: 'https://www.linkedin.com/sales/company/123456789',
              name: 'salesNavigator',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.sitemap',
              example: 'https://www.jaygatsbyenterprises.com/sitemap.xml',
              name: 'sitemap',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.status',
              example: 'https://status.jaygatsbyenterprises.com',
              name: 'status',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.legals.terms',
              example: 'https://www.jaygatsbyenterprises.com/terms',
              name: 'terms',
              parent: `${parent}.legals`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.updates',
              example: 'https://www.jaygatsbyenterprises.com/updates',
              name: 'updates',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.website',
              example: 'https://www.jaygatsbyenterprises.com',
              name: 'website',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.wellfound',
              example: 'https://wellfound.com/company/jaygatsbyenterprises',
              name: 'wellfound',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.wikidata',
              example: 'https://www.wikidata.org/wiki/Jay_Gatsby_Enterprises',
              name: 'wikidata',
              parent: `${parent}.urls`,
              type: 'string',
            },
            {
              description: 'api.company.v2.urls.wikipedia',
              example: 'https://en.wikipedia.org/wiki/Jay_Gatsby_Enterprises',
              name: 'wikipedia',
              parent: `${parent}.urls`,
              type: 'string',
            },
          ],
        },
      )

      if (params.simplified) {
        attributes = attributes
          .map((section) => {
            section.attributes = section.attributes?.filter((attribute) => {
              if (params.simplified) {
                return attribute.simplified
              }

              return false
            })

            return section
          })
          .filter(section => section.attributes?.length)
      }
    }

    return attributes
  }

  const getCompanyAppAttributes = (params: {
    example: Record<string, number | string>
    parent: string
  }) => {
    const attributes: ApiAttribute[] = [
      {
        description: 'api.company.v2.apps.global.id',
        example: params.example.id || '',
        name: 'id',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.apps.global.name',
        example: params.example.name || '',
        name: 'name',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.apps.global.rating',
        example: params.example.rating,
        name: 'rating',
        parent: params.parent,
        type: 'number',
      },
      {
        description: 'api.company.v2.apps.global.reviews',
        example: params.example.reviews,
        name: 'reviews',
        parent: params.parent,
        type: 'number',
      },
      {
        description: 'api.company.v2.apps.global.type',
        example: params.example.type || '',
        name: 'type',
        parent: params.parent,
        type: 'string',
        values: [
          'app',
          'developer',
          'extension',
        ],
      },
      {
        description: 'api.company.v2.apps.global.url',
        example: params.example.url || '',
        name: 'url',
        parent: params.parent,
        type: 'string',
      },
    ]

    return attributes
  }

  const getCompanyCompaniesAttributes = (params: {
    example: Record<string, string>
    parent: string
  }) => {
    const attributes: ApiAttribute[] = [
      {
        description: 'api.company.v2.companies.globals.description',
        example: params.example.description || '',
        name: 'description',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.companies.globals.domain',
        example: params.example.domain || '',
        name: 'domain',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.companies.globals.name',
        example: params.example.name || '',
        name: 'name',
        parent: params.parent,
        type: 'string',
      },
    ]

    return attributes
  }

  const getCompanyContactAttributes = (params: {
    example: Record<string, string>
    parent: string
  }) => {
    const attributes: ApiAttribute[] = [
      {
        description: 'api.company.v2.contacts.globals.category',
        example: params.example.category || '',
        name: 'category',
        parent: params.parent,
        type: 'string',
        values: [
          'accounts',
          'channels',
          'general',
          'marketing',
          'reservations',
          'sales',
          'technical',
        ],
      },
      {
        description: 'api.company.v2.contacts.globals.value',
        example: params.example.value || '',
        name: 'value',
        parent: params.parent,
        type: 'string',
      },
    ]

    return attributes
  }

  const getCompanyPodcastAttributes = (params: {
    examples?: Record<string, number | string>
    parent: string
  }) => {
    const attributes: ApiAttribute[] = [
      {
        description: 'api.company.v2.contents.podcasts.globals.id',
        example: params.examples?.id || '',
        name: 'id',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.contents.podcasts.globals.name',
        example: params.examples?.name || '',
        name: 'name',
        parent: params.parent,
        type: 'string',
      },
      {
        description: 'api.company.v2.contents.podcasts.globals.rating',
        example: params.examples?.rating,
        name: 'rating',
        parent: params.parent,
        type: 'number',
      },
      {
        description: 'api.company.v2.contents.podcasts.globals.reviews',
        example: params.examples?.reviews,
        name: 'reviews',
        parent: params.parent,
        type: 'number',
      },
      {
        description: 'api.company.v2.contents.podcasts.globals.url',
        example: params.examples?.url || '',
        name: 'url',
        parent: params.parent,
        type: 'string',
      },
    ]

    return attributes
  }

  const getCompanyLocationsGeopointAttributes = (params: {
    example: {
      lat: number
      lon: number
    }
    parent: string
  }) => {
    const attributes: ApiAttribute[] = [
      {
        description: 'api.company.v2.locations.globals.geopoint.lat',
        example: params.example.lat,
        name: 'lat',
        parent: params.parent,
        type: 'number',
      },
      {
        description: 'api.company.v2.locations.globals.geopoint.lon',
        example: params.example.lon,
        name: 'lon',
        parent: params.parent,
        type: 'number',
      },
    ]

    return attributes
  }

  const getCompanySocialAttributes = (params: {
    attributes?: Array<Exclude<keyof CompanyV2Social, 'id' | 'url'>>
    example: Record<string, number | string>
    parent: string
  }) => {
    const attributes: ApiAttribute[] = []

    if (params.attributes?.includes('followers')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.followers',
        example: params.example.followers,
        name: 'followers',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('following')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.following',
        example: params.example.following,
        name: 'following',
        parent: params.parent,
        type: 'number',
      })
    }

    attributes.push({
      description: 'api.company.v2.socials.globals.id',
      example: params.example.id,
      name: 'id',
      parent: params.parent,
      type: 'string',
    })

    if (params.attributes?.includes('idNumeric')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.idNumeric',
        example: params.example.idNumeric,
        name: 'idNumeric',
        parent: params.parent,
        type: 'string',
      })
    }

    if (params.attributes?.includes('likes')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.likes',
        example: params.example.likes,
        name: 'likes',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('members')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.members',
        example: params.example.members,
        name: 'members',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('monthlyViews')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.monthlyViews',
        example: params.example.monthlyViews,
        name: 'monthlyViews',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('posts')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.posts',
        example: params.example.posts,
        name: 'posts',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('rating')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.rating',
        example: params.example.rating,
        name: 'rating',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('reviews')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.reviews',
        example: params.example.reviews,
        name: 'reviews',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('talkingAbout')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.talkingAbout',
        example: params.example.talkingAbout,
        name: 'talkingAbout',
        parent: params.parent,
        type: 'number',
      })
    }

    attributes.push({
      description: 'api.company.v2.socials.globals.url',
      example: params.example.url,
      name: 'url',
      parent: params.parent,
      type: 'string',
    })

    if (params.attributes?.includes('videos')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.videos',
        example: params.example.videos,
        name: 'videos',
        parent: params.parent,
        type: 'number',
      })
    }

    if (params.attributes?.includes('wereHere')) {
      attributes.push({
        description: 'api.company.v2.socials.globals.wereHere',
        example: params.example.wereHere,
        name: 'wereHere',
        parent: params.parent,
        type: 'number',
      })
    }

    return attributes
  }

  const getContinentAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'company'

    attributes.push(
      {
        description: 'api.continent.code',
        name: 'code',
        parent: `${parent}.continent`,
        type: 'string',
      },
      {
        description: 'api.continent.latitude',
        name: 'latitude',
        parent: `${parent}.continent`,
        type: 'string',
      },
      {
        description: 'api.continent.longitude',
        name: 'longitude',
        parent: `${parent}.continent`,
        type: 'string',
      },
      {
        description: 'api.continent.name',
        name: 'name',
        parent: `${parent}.continent`,
        type: 'string',
      },
      {
        description: 'api.continent.nameEs',
        name: 'nameEs',
        parent: `${parent}.continent`,
        type: 'string',
      },
      {
        description: 'api.continent.nameFr',
        name: 'nameFr',
        parent: `${parent}.continent`,
        type: 'string',
      },
    )

    return attributes
  }

  const getCountryAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'company'

    attributes.push(
      {
        description: 'api.country.code',
        name: 'code',
        parent: `${parent}.country`,
        type: 'string',
      },
      {
        description: 'api.country.latitude',
        name: 'latitude',
        parent: `${parent}.country`,
        type: 'string',
      },
      {
        description: 'api.country.longitude',
        name: 'longitude',
        parent: `${parent}.country`,
        type: 'string',
      },
      {
        description: 'api.country.name',
        name: 'name',
        parent: `${parent}.country`,
        type: 'string',
      },
      {
        description: 'api.country.nameEs',
        name: 'nameEs',
        parent: `${parent}.country`,
        type: 'string',
      },
      {
        description: 'api.country.nameFr',
        name: 'nameFr',
        parent: `${parent}.country`,
        type: 'string',
      },
    )

    return attributes
  }

  const getCountyAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'company'

    attributes.push(
      {
        description: 'api.county.code',
        name: 'code',
        parent: `${parent}.county`,
        type: 'string',
      },
      {
        description: 'api.county.latitude',
        name: 'latitude',
        parent: `${parent}.county`,
        type: 'string',
      },
      {
        description: 'api.county.longitude',
        name: 'longitude',
        parent: `${parent}.county`,
        type: 'string',
      },
      {
        description: 'api.county.name',
        name: 'name',
        parent: `${parent}.county`,
        type: 'string',
      },
    )

    return attributes
  }

  const getActionAttributes = () => {
    const attributes: ApiAttribute[] = []

    attributes.push(
      {
        description: 'api.action.status',
        example: 'pending',
        name: 'status',
        type: 'string',
        values: [
          'pending',
          'active',
          'completed',
          'failed',
        ],
      },
      {
        description: 'api.action.cost',
        example: 10,
        name: 'cost',
        type: 'number',
      },
      {
        description: 'api.action.type',
        name: 'type',
        type: 'string',
        values: ['jobs:request'],
      },
      {
        description: 'api.action.data._',
        name: 'data',
        optional: true,
        type: 'object',

        attributes: [
          {
            description: 'api.action.data.domains',
            name: 'domains',
            optional: true,
            type: 'array',
          },
          {
            description: 'api.action.data.job',
            name: 'job',
            optional: true,
            type: 'string',
            values: ['enrich-companies', 'more-to-come'],
          },
        ],
      },
      {
        description: 'api.action.createdAt',
        example: '2024-08-23T17:02:47.296+02:00',
        name: 'createdAt',
        type: 'date',
      },
      {
        description: 'api.action.updatedAt',
        example: '2024-08-23T17:02:47.296+02:00',
        name: 'updatedAt',
        type: 'date',
      },
    )

    return attributes
  }

  const getIndustryAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'industry'

    attributes.push(
      {
        description: 'api.industry.companiesCount',
        example: 643783,
        name: 'companiesCount',
        parent,
        type: 'number',
      },
      {
        description: 'api.industry.name',
        example: 'health care',
        name: 'name',
        parent,
        type: 'string',
      },
      {
        description: 'api.industry.slug',
        example: 'health-care',
        name: 'slug',
        parent,
        type: 'string',
      },
    )

    return attributes
  }

  const getJobTitleAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'jobTitle'

    attributes.push(
      {
        description: 'api.jobTitle.department',
        example: 'marketing',
        name: 'department',
        parent,
        type: 'string',
        values: [
          'customer service',
          'design',
          'education',
          'engineering',
          'finance',
          'health',
          'human resources',
          'legal',
          'marketing',
          'media',
          'operations',
          'public relations',
          'real estate',
          'sales',
          'trades',
        ],
      },
      {
        description: 'api.jobTitle.departmentSecondary',
        example: 'content marketing',
        name: 'departmentSecondary',
        parent,
        type: 'string',
        values: [
          'accounting',
          'accounts',
          'brand marketing',
          'broadcasting',
          'business development',
          'compensation',
          'content marketing',
          'customer success',
          'data',
          'dental',
          'devops',
          'doctor',
          'editorial',
          'education administration',
          'electrical',
          'employee development',
          'events',
          'fitness',
          'graphic design',
          'information technology',
          'investment',
          'journalism',
          'judicial',
          'lawyer',
          'logistics',
          'mechanical',
          'media relations',
          'network',
          'nursing',
          'office management',
          'paralegal',
          'pipeline',
          'product',
          'product design',
          'product marketing',
          'professor',
          'project engineering',
          'project management',
          'property management',
          'quality assurance',
          'realtor',
          'recruiting',
          'researcher',
          'security',
          'software',
          'support',
          'systems',
          'tax',
          'teacher',
          'therapy',
          'video',
          'web',
          'web design',
          'wellness',
          'writing',
        ],
      },
      {
        description: 'api.jobTitle.name',
        name: 'name',
        parent,
        type: 'string',
      },
      {
        description: 'api.jobTitle.nameEs',
        name: 'nameEs',
        parent,
        type: 'string',
      },
      {
        description: 'api.jobTitle.nameFr',
        name: 'nameFr',
        parent,
        type: 'string',
      },
      {
        description: 'api.jobTitle.seniorityLevel',
        name: 'seniorityLevel',
        parent,
        type: 'string',
        values: [
          'owner',
          'partner',
          'cxo',
          'vp',
          'director',
          'manager',
          'senior',
          'entry',
          'training',
          'unpaid',
        ],
      },
    )

    return attributes
  }

  const getListAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'list'

    attributes.push(
      {
        description: 'api.list.analytics',
        name: 'analytics',
        parent: `${parent}`,
        type: 'object',

        attributes: [
          {
            description: 'api.list.analyticsCompaniesCount',
            example: 10000,
            name: 'companiesCount',
            parent: `${parent}.analytics`,
            type: 'number',
          },
        ],
      },
      {
        description: 'api.list.createdAt',
        example: '2022-02-22T07:23:52.000+00:00',
        name: 'createdAt',
        parent: `${parent}`,
        type: 'date',
      },
      {
        description: 'api.list.dynamic',
        example: true,
        name: 'dynamic',
        parent: `${parent}`,
        type: 'boolean',
      },
      {
        description: 'api.list.id',
        example: 3838,
        name: 'id',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.list.imported',
        example: true,
        name: 'imported',
        parent: `${parent}`,
        type: 'boolean',
      },
      {
        description: 'api.list.integrations',
        name: 'integrations',
        parent: `${parent}`,
        type: 'array',

        attributes: [
          {
            description: 'api.list.integrationsApiKey',
            example: 'My_AP1_T0k3n',
            name: 'apiKey',
            parent: `${parent}.integrations`,
            type: 'string',
          },
          {
            description: 'api.list.integrationsId',
            example: 14,
            name: 'id',
            parent: `${parent}.integrations`,
            type: 'number',
          },
          {
            description: 'api.list.integrationsSlug',
            example: 'la-growth-machine',
            name: 'slug',
            parent: `${parent}.integrations`,
            type: 'string',
          },
          {
            description: 'api.list.integrationsStatus',
            example: 'connected',
            name: 'status',
            parent: `${parent}.integrations`,
            type: 'string',
            values: [
              'connected',
              'disconnected',
              'failed',
            ],
          },
          {
            description: 'api.list.integrationsStatusDetails',
            example: 'Everything is fine',
            name: 'statusDetails',
            parent: `${parent}.integrations`,
            type: 'string',
          },
        ],
      },
      {
        description: 'api.list.name',
        example: 'SaaS with 100k+ MRR',
        name: 'name',
        parent: `${parent}`,
        type: 'string',
      },
      {
        description: 'api.list.processActive',
        example: true,
        name: 'processActive',
        parent: `${parent}`,
        type: 'boolean',
      },
      {
        description: 'api.list.processInitialized',
        example: true,
        name: 'processInitialized',
        parent: `${parent}`,
        type: 'boolean',
      },
      {
        description: 'api.list.processingAt',
        example: '2022-02-22T07:23:52.000+00:00',
        name: 'processingAt',
        parent: `${parent}`,
        type: 'date',
      },
      {
        description: 'api.list.query',
        name: 'query',
        parent: `${parent}`,
        type: 'array',

        attributes: getQueryAttributes({
          attributes: [],
          parent: `${parent}.query`,
          values: null,
        }),
      },
    )

    return attributes
  }

  const getMetaAttributes = (params?: {
    parent?: string
    specialFields?: string[]
  }): ApiAttribute => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'meta'
    const specialFields = params?.specialFields || []

    if (specialFields.includes('creditsConsumed')) {
      attributes.push({
        description: 'api.meta.creditsConsumed',
        name: 'creditsConsumed',
        parent: `${parent}`,
        type: 'number',
      })
    }

    if (specialFields.includes('creditsRemaining')) {
      attributes.push({
        description: 'api.meta.creditsRemaining',
        name: 'creditsRemaining',
        parent: `${parent}`,
        type: 'number',
      })
    }

    attributes.push(
      {
        description: 'api.meta.currentPage',
        name: 'currentPage',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.meta.firstPage',
        name: 'firstPage',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.meta.lastPage',
        name: 'lastPage',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.meta.perPage',
        name: 'perPage',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.meta.total',
        name: 'total',
        parent: `${parent}`,
        type: 'number',
      },
    )

    if (specialFields.includes('totalWithEmail')) {
      attributes.push({
        description: 'api.meta.totalWithEmail',
        name: 'totalWithEmail',
        parent: `${parent}`,
        type: 'number',
      })
    }

    return {
      attributes,
      description: 'api.meta._',
      name: `${parent}`,
      type: 'object',
    }
  }

  const getMetaAnalyticsAttributes = (params?: {
    parent?: string
  }): ApiAttribute => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'meta'

    attributes.push(
      {
        description: 'api.metaAnalytics.invalidListIds',
        name: 'invalidListIds',
        parent: `${parent}`,
        type: 'array',
      },
      {
        description: 'api.metaAnalytics.listIds',
        name: 'listIds',
        parent: `${parent}`,
        type: 'array',
      },
      {
        attributes: getQueryAttributes({
          attributes: [],
          parent: `${parent}.query`,
          values: null,
        }),
        description: 'api.metaAnalytics.query',
        name: 'query',
        parent: `${parent}`,
        type: 'array',
      },
      {
        description: 'api.metaAnalytics.totalDatapoints',
        name: 'totalDatapoints',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.metaAnalytics.totalDocuments',
        name: 'totalDocuments',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.metaAnalytics.totalValues',
        name: 'totalValues',
        parent: `${parent}`,
        type: 'number',
      },
    )

    return {
      attributes,
      description: 'api.metaAnalytics._',
      name: `${parent}`,
      type: 'object',
    }
  }

  const getQueryAttributes = (params?: {
    attributes?: string[]
    parent?: string
    values?: null | string[]
  }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'condition'

    if (!params?.attributes || !params?.attributes?.length) {
      if (!params) {
        params = {}
      }

      params.attributes = segmentation.attributesWeCanQuery
    }

    attributes.push(
      {
        description: 'api.condition.attribute',
        example: params.attributes[0],
        name: 'attribute',
        parent,
        type: 'string',
        values: params.attributes,
      },
      {
        description: 'api.condition.operator',
        example: 'or',
        name: 'operator',
        parent,
        type: 'string',
        values: [...segmentation.operators],
      },
      {
        description: 'api.condition.sign',
        example: 'equals',
        name: 'sign',
        parent,
        type: 'string',
        values: [...segmentation.signs],
      },
      {
        description: 'api.condition.value',
        example: params.values || [
          'saas',
          'cloud',
          'blockchain',
        ],
        name: 'value',
        parent,
        type: 'array',
      },
    )

    return attributes
  }

  const getSortAttributes = (params: {
    endpoint: string
  }) => {
    const { t } = useI18n()

    const attributes: ApiAttribute[] = [
      {
        description: 'api.sort.sortFields._',
        example: `…${params.endpoint}?sortFields=%5B%7B%22key%22%3A%22meta.score%22%2C%22order%22%3A%22desc%22%7D%5D`,
        name: 'sortFields',
        optional: true,
        type: 'string',
        warning: t('globals.stringifyAndEncode'),

        attributes: [
          {
            description: 'api.sort.sortFields.key',
            name: 'key',
            type: 'string',
            values: segmentation.attributesWeCanSort,
          },
          {
            description: 'api.sort.sortFields.order',
            name: 'order',
            type: 'string',
            values: [...segmentation.sortOrder],
          },
        ],
      },
      {
        description: 'api.sort.sortKey',
        example: `…${params.endpoint}?sortKey=meta.score`,
        name: 'sortKey',
        optional: true,
        type: 'string',
        values: segmentation.attributesWeCanSort,
      },
      {
        description: 'api.sort.sortOrder',
        example: `…${params.endpoint}?sortOrder=asc`,
        hasDivider: false,
        name: 'sortOrder',
        optional: true,
        type: 'string',
        values: [...segmentation.sortOrder],
      },
    ]

    return attributes
  }

  const getStateAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'company'

    attributes.push(
      {
        description: 'api.state.code',
        name: 'code',
        parent: `${parent}.state`,
        type: 'string',
      },
      {
        description: 'api.state.latitude',
        name: 'latitude',
        parent: `${parent}.state`,
        type: 'string',
      },
      {
        description: 'api.state.longitude',
        name: 'longitude',
        parent: `${parent}.state`,
        type: 'string',
      },
      {
        description: 'api.state.name',
        name: 'name',
        parent: `${parent}.state`,
        type: 'string',
      },
    )

    return attributes
  }

  const getTeamAttributes = () => {
    const attributes: ApiAttribute[] = []

    attributes.push(
      {
        description: 'api.team.country',
        example: 'us',
        name: 'country',
        type: 'string',
      },
      {
        description: 'api.team.createdAt',
        example: '2020-06-29 08:21:01',
        name: 'createdAt',
        type: 'date',
      },
      {
        description: 'api.team.credits',
        example: 500 * 1000,
        name: 'credits',
        type: 'number',
      },
      {
        description: 'api.team.isSubscriber',
        example: true,
        name: 'isSubscriber',
        type: 'boolean',
      },
      {
        description: 'api.team.id',
        example: 1,
        name: 'id',
        type: 'number',
      },
      {
        description: 'api.team.name',
        example: 'Jay Gatsby Enterprises',
        name: 'name',
        type: 'string',
      },
      {
        description: 'api.team.websiteUrl',
        example: 'https://www.jaygatsbyenterprises.com',
        name: 'websiteUrl',
        type: 'string',
      },
    )

    return attributes
  }

  const getTechnologyAttributes = (params?: { parent?: string }) => {
    const attributes: ApiAttribute[] = []
    const parent = params?.parent || 'technology'

    attributes.push(
      {
        description: 'api.technology.categories',
        example: [
          'commerce',
          'e-commerce',
          'e-commerce-platforms',
        ],
        name: 'categories',
        parent: `${parent}`,
        type: 'array',
      },
      {
        description: 'api.technology.companiesCount',
        example: 643783,
        name: 'companiesCount',
        parent,
        type: 'number',
      },
      {
        description: 'api.technology.editor',
        example: 'vercel.com',
        name: 'editor',
        parent,
        type: 'string',
      },
      {
        description: 'api.technology.free',
        example: true,
        name: 'false',
        parent,
        type: 'boolean',
      },
      {
        description: 'api.technology.id',
        example: 260,
        name: 'id',
        parent: `${parent}`,
        type: 'number',
      },
      {
        description: 'api.technology.name',
        example: 'Shopify',
        name: 'name',
        parent: `${parent}`,
        type: 'string',
      },
      {
        description: 'api.technology.nameSynonyms',
        example: ['Snowdevil'],
        name: 'nameSynonyms',
        parent: `${parent}`,
        type: 'array',
      },
      {
        description: 'api.technology.paid',
        example: true,
        name: 'paid',
        parent,
        type: 'boolean',
      },
      {
        description: 'api.technology.slug',
        example: 'shopify',
        name: 'slug',
        parent,
        type: 'string',
      },
      {
        description: 'api.technology.websiteUrl',
        example: 'https://www.shopify.com',
        name: 'websiteUrl',
        parent,
        type: 'string',
      },
    )

    return attributes
  }

  const getEmailAttributes = (params: {
    email: string
  }) => {
    const attributes: ApiAttribute[] = []

    const [name, domain] = params.email.split('@')

    attributes.push(
      {
        description: 'api.email.address',
        example: params.email,
        name: 'address',
        type: 'string',
      },
      {
        description: 'api.email.domain',
        example: domain,
        name: 'domain',
        type: 'string',
      },
      {
        description: 'api.email.fullName._',
        name: 'fullName',
        type: 'object',

        attributes: [
          {
            description: 'api.email.fullName.first',
            example: 'John',
            name: 'first',
            type: 'string',
          },
          {
            description: 'api.email.fullName.last',
            example: 'Doe',
            name: 'last',
            type: 'string',
          },
          {
            description: 'api.email.fullName.middle',
            example: 'Michael',
            name: 'middle',
            type: 'string',
          },
        ],
      },
      {
        description: 'api.email.isDisposable',
        example: false,
        name: 'isDisposable',
        type: 'boolean',
      },
      {
        description: 'api.email.isFree',
        example: false,
        name: 'isFree',
        type: 'boolean',
      },
      {
        description: 'api.email.isSubaddress',
        example: false,
        name: 'isSubaddress',
        type: 'boolean',
      },
      {
        description: 'api.email.isValid',
        example: true,
        name: 'isValid',
        type: 'boolean',
      },
      {
        description: 'api.email.isValidFormat',
        example: true,
        name: 'isValidFormat',
        type: 'boolean',
      },
      {
        description: 'api.email.name',
        example: name,
        name: 'name',
        type: 'string',
      },
      {
        description: 'api.email.pattern',
        example: '[F].[M].[L]',
        name: 'pattern',
        type: 'string',
      },
    )

    return attributes
  }

  return {
    getActionAttributes,
    getCompanyAttributes,
    getCompanySocialAttributes,
    getContinentAttributes,
    getCountryAttributes,
    getCountyAttributes,
    getEmailAttributes,
    getIndustryAttributes,
    getJobTitleAttributes,
    getListAttributes,
    getMetaAnalyticsAttributes,
    getMetaAttributes,
    getQueryAttributes,
    getSortAttributes,
    getStateAttributes,
    getTeamAttributes,
    getTechnologyAttributes,
  }
}
